import { useState, useEffect, useCallback } from 'react';
import { Spin, Pagination, Empty, DatePicker, Tag, Select, Button, Input, Row, Col } from 'antd';
import axios from 'axios';

import Sidebar from '../Sidebar/Sidebar';
import ViewImagesModal from './ViewImagesModal';
import ViewImagesButton from './ViewImagesButton';

import './DeviceReports.css';
import searchIcon from '../Dashboard/assets/img/search.png';

const { RangePicker } = DatePicker;
const { Option } = Select;

function GradeCompareReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [ngrades, setNGrades] = useState({});
  const [agentData, setAgentData] = useState({});
  const [filters, setFilters] = useState({ brand: '', model: '', oPrice: '', nPrice: '' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = 12;

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedIMEI, setSelectedIMEI] = useState(null);


  // ✅ Fetch Reports and Grades Together
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/reports/dashboard/grade');
      const fetchedReports = await Promise.all(
        response.data.reports.map(async (report) => {
          const imei = report.data.report.IMEI || '-';
          const nGrade = report.data.report.grade || '-';
          const oGradePrice = await fetchOGradePrice(imei);
          const nGradePrice = await fetchNGradePrice(imei, nGrade);
          const sessionId = await fetchSessionID(imei);
          const sessionSource = await fetchSource(imei);

          // ✅ Calculate price difference
          let priceDifference = '-';
          if (!isNaN(parseFloat(nGradePrice)) && !isNaN(parseFloat(oGradePrice))) {
            const diff = parseFloat(nGradePrice) - parseFloat(oGradePrice);
            priceDifference = diff >= 0 ? `+${diff.toFixed(2)}` : `${diff.toFixed(2)}`;
          }
          const gradNamesObj = {
            1: "newPrice",
            2: "goodPrice",
            3: "badPrice",
            4: "recyclePrice"
          };
          console.log("Report Data:", report.data.report); // 🛠
          return {
            sessionId,
            reportId: report.data.report.reportid || '-',
            reportDate: report.data.report.finishtime || '-',
            imei,
            brand: report.data.report.Manufacturer || '-',
            model: report.data.report.Model || '-',
            oGradePrice,
            nGradePrice,
            grade: gradNamesObj[Number(nGrade)] || '-',
            priceDifference,
            status: report.data.report.reportstatus || '-',
            imageLink: report.data.report.imageLink || '-',
            videoLink: report.data.report.videoLink || '-',
            source: sessionSource || 'CP Store',
          };
        })
      );

      setReports(fetchedReports);
      await fetchGrades(fetchedReports);

      fetchedReports.forEach((report) => {
        fetchAgent(report.imei);
      });
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Fetch Grades Efficiently Using Cache
  const fetchGrades = async (reports) => {
    const cache = {};
    const newGrades = {};

    await Promise.all(
      reports.map(async (report) => {
        if (!report.imei || cache[report.imei] !== undefined) {
          newGrades[report.imei] = cache[report.imei] || 'N/A';
          return;
        }

        try {
          const result = await axios.get(
            `https://live.callcenter.buybackbooth.com/Scripts/Gearvana/CC_GetSessionFromIMEI.php?imei=${report.imei}`
          );

          if (result.data.status === 'success' && result.data.data.length > 0) {
            newGrades[report.imei] = result.data.data[0]?.grading?.grade || 'N/A';
          } else {
            newGrades[report.imei] = 'N/A';
          }
        } catch (error) {
          newGrades[report.imei] = 'N/A';
        }

        cache[report.imei] = newGrades[report.imei];
      })
    );
    setNGrades(newGrades);
  };

  const fetchOGradePrice = async (imei) => {
    if (!imei || imei === '-') return '-'; // Handle missing IMEI

    try {
      const response = await axios.get(
        `https://live.callcenter.buybackbooth.com/Scripts/Gearvana/CC_GetSessionFromIMEI.php?imei=${imei}`
      );

      const price = response.data?.data?.[0]?.price;

      console.log(`Fetched O-Grade Price for IMEI ${imei}:`, price); // 🛠 Debug Log

      // ✅ Explicitly check if the price is 0, otherwise return price or '-'
      return price !== undefined && price !== null ? price : '-';
    } catch (error) {
      console.error(`Error fetching O-Grade price for IMEI ${imei}:`, error);
      return '-';
    }
  };


  const fetchNGradePrice = async (imei, nGrade) => {
    if (!imei || imei === '-' || !nGrade) return '-'; // Handle missing IMEI or N-Grade

    try {
      const response = await axios.get(
        `https://live.callcenter.buybackbooth.com/Scripts/Gearvana/CC_GetSessionFromIMEI.php?imei=${imei}`
      );

      const allPrices = response.data?.data?.[0]?.allPrices;
      if (!allPrices) return '-'; // If allPrices is missing, return '-'

      // Map N-Grade to the correct price
      switch (nGrade) {
        case '1':
          return allPrices.new ?? '-';
        case '2':
          return allPrices.good ?? '-';
        case '3':
          return allPrices.bad ?? '-';
        default:
          return '-';
      }
    } catch (error) {
      console.error(`Error fetching N-Grade price for IMEI ${imei}:`, error);
      return '-';
    }
  };

  const fetchAgent = async (imei) => {
    if (!imei) return '-';
    try {
      const response = await axios.get(
        `https://live.callcenter.buybackbooth.com/Scripts/Gearvana/CC_GetSessionFromIMEI.php?imei=${imei}`
      );
      const agent = response.data?.data?.[0]?.agent || 'N/A';
      setAgentData((prev) => ({
        ...prev,
        [imei]: agent,
      }));
    } catch (error) {
      console.error(`Error fetching agent for IMEI ${imei}:`, error);
      setAgentData((prev) => ({
        ...prev,
        [imei]: 'N/A',
      }));
    }
  };

  const fetchSessionID = async (imei) => {
    if (!imei) return null;

    try {
      const response = await axios.get(
        `https://live.callcenter.buybackbooth.com/Scripts/Gearvana/CC_GetSessionFromIMEI.php?imei=${imei}`
      );

      if (response.data.status === 'success' && response.data.data.length > 0) {
        return response.data.data[0].sessionId || 'N/A';
      }
    } catch (error) {
      console.error(`Error fetching session ID for IMEI: ${imei}`, error);
    }

    return 'N/A';
  };

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // ✅ Filter Reports Based on Search and Date Range
  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      report.reportId.toString().includes(searchQuery) ||
      report.imei.toString().includes(searchQuery);

    const matchesFilters =
      (!filters.brand || report.brand.includes(filters.brand)) &&
      (!filters.model || report.model.includes(filters.model)) &&
      (!filters.oPrice || report.oGradePrice == filters.oPrice) &&
      (!filters.nPrice || report.nGradePrice == filters.nPrice);

    if (!dateRange[0] || !dateRange[1]) return matchesSearch && matchesFilters;

    const reportDate = new Date(report.reportDate);
    return (
      matchesSearch &&
      matchesFilters &&
      reportDate >= dateRange[0].startOf('day').toDate() &&
      reportDate <= dateRange[1].endOf('day').toDate()
    );
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };
  // ✅ Handle Search Change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedIMEI(null);
  };

  // ✅ Handle Page Change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // ✅ Get Current Reports for Pagination
  const currentReports = filteredReports.slice(
    (currentPage - 1) * reportsPerPage,
    currentPage * reportsPerPage
  );

  const fetchSource = async (imei) => {
    try {
      const result = await axios.get(
        `https://live.callcenter.buybackbooth.com/Scripts/Gearvana/CC_GetSessionFromIMEI.php?imei=${imei}`
      );

      if (result.data.status === 'error' && result.data.message.includes("No sessions found")) {
        return 'CP Store';
      } else if (result.data.status === 'success' && result.data.data.length > 0) {
        return 'LIVE';
      }
      return 'CP Store';
    } catch (error) {
      console.error(`Error fetching source for IMEI ${imei}:`, error);
      return 'CP Store';
    }
  };


  return (
    <div className="App grade-compare-reports" >
      <div className="dashboard">
        <div className="container-fluid">
          <div className="row">
            <Sidebar />
            <div className="col-md-10 main" style={{marginTop:-20}}>
              <h1 className="page-heading">Grading Insights</h1>

              {/* Search and Date Filters */}
              <div >
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                  <Col xs={24} sm={12} md={8}>
                    <div className="input-group">
                      <span className="input-group-text">
                        <img src={searchIcon} alt="Search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter IMEI/Report ID/Session ID..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>

                    <div className="input-group">
                      <RangePicker
                        onChange={(dates) => setDateRange(dates || [null, null])}
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              {/* <div className="filters">
               
                <Select placeholder="Brand" onChange={(value) => handleFilterChange('brand', value)}>
                  {[...new Set(reports.map((r) => r.brand))].map((brand) => (
                    <Option key={brand} value={brand}>{brand}</Option>
                  ))}
                </Select>
                <Select placeholder="Model" onChange={(value) => handleFilterChange('model', value)}>
                  {[...new Set(reports.map((r) => r.model))].map((model) => (
                    <Option key={model} value={model}>{model}</Option>
                  ))}
                </Select>
                <Select
  placeholder="Select O-Grade"
  onChange={(value) => handleFilterChange("oPrice", value)}
  value={filters.oPrice}
  style={{ width: 150, marginRight: 10 }}
>
  <Option value="">All</Option>
  <Option value="newPrice">New Price</Option>
  <Option value="goodPrice">Good Price</Option>
  <Option value="badPrice">Bad Price</Option>
</Select>

<Select
  placeholder="Select N-Grade"
  onChange={(value) => handleFilterChange("nPrice", value)}
  value={filters.nPrice}
  style={{ width: 150 }}
>
  <Option value="">All</Option>
  <Option value="1">1</Option>
  <Option value="2">2</Option>
  <Option value="3">3</Option>
</Select>

              </div> */}

              {/* Reports Table */}
              <section className="reports">
                <div className='container'>

                  <div className='row align-items-center'>

                    <div className='col-12'>


                      <div className='table-responsive'>

                        <Spin spinning={loading} size="large">
                          <table className="table table-striped table-bordered align-items-center">
                            <thead>
                              <tr>
                                <th>Session ID</th>
                                <th>Report ID</th>
                                <th>IMEI</th>
                                <th>Brand</th>
                                <th>Model</th>
                                <th>O-Grade</th>
                                <th>O-Grade Price</th>
                                <th>Agent</th>
                                <th>N-Grade</th>
                                <th>N-Grade Price</th>
                                <th>Price Difference</th>
                                <th>Source</th>
                                <th>Image Link</th>
                                <th>Video Link</th>
                                <th>Image Compare</th>
                              
                              </tr>
                            </thead>
                            <tbody>
                              {currentReports.length > 0 ? (
                                currentReports.map((report) => (
                                  <tr key={report.reportId}>
                                    <td>{report.sessionId}</td>
                                    <td>{report.reportId}</td>
                                    <td>{report.imei}</td>
                                    <td>{report.brand}</td>
                                    <td>{report.model}</td>
                                    <td>{ngrades[report.imei] || 'N/A'}</td>
                                    <td>${report.oGradePrice ?? '-'}</td>
                                    <td>{agentData[report.imei] || '-'}</td>
                                    <td>{report.grade}</td>
                                    <td>${report.nGradePrice || '-'}</td>
                                    <td>
                                      {report.priceDifference !== undefined ? (
                                        <Tag color={report.priceDifference > 0 ? 'green' : report.priceDifference < 0 ? 'red' : 'default'}>
                                          ${report.priceDifference}
                                        </Tag>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                    <td>
                                      <Tag color={report.source === 'LIVE' ? 'gold' : 'orange'}>{report.source}</Tag>
                                    </td>
                                    <td>
                                      {report.imageLink && report.imageLink !== '-' ? (
                                        <a href={report.imageLink} target="_blank" rel="noopener noreferrer">
                                          View Images
                                        </a>
                                      ) : (
                                        <span style={{ color: '#d3d3d3' }}>No Images Available</span>
                                      )}
                                    </td>
                                    <td>
                                      {report.videoLink && report.videoLink !== '-' ? (
                                        <a href={report.videoLink} target="_blank" rel="noopener noreferrer">
                                          View Video
                                        </a>
                                      ) : (
                                        <span style={{ color: '#d3d3d3' }}>No Video Available</span>
                                      )}
                                    </td>
                                    <td>
                                      <ViewImagesButton imei={report.imei} />
                                    </td>
                                  
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="14">
                                    <Empty description="No reports found!" />
                                  </td>
                                </tr>
                              )}
                            </tbody>


                          </table>
                        </Spin>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='table-bottom'>
                  <div className='pagination-container'>

                    {/* Pagination */}
                    <Pagination
                      current={currentPage}
                      pageSize={reportsPerPage}
                      total={filteredReports.length}
                      onChange={handlePageChange}
                    />
                  </div>
                </div>
              </section>

              {/* Modal */}

              <ViewImagesModal
                imei={selectedIMEI}
                isVisible={isModalVisible}
                onClose={closeModal}
              />
            </div>
          </div >
        </div >
      </div >
    </div >
  );
}

export default GradeCompareReports;
